import { useState } from "react";
import { Check, ChevronsUpDown, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { INITIAL_EXPENSE_TYPES } from "@/hooks/useExpenses";
import { ColorPickerPopover } from "./ColorPickerPopover";

interface ExpenseTypeComboboxProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  onManage?: () => void;
  getColor?: (value: string) => { bg: string; text: string; dot: string };
  onColorChange?: (value: string, color: string) => void;
}

export const ExpenseTypeCombobox = ({
  value,
  options,
  onChange,
  onManage,
  getColor,
  onColorChange,
}: ExpenseTypeComboboxProps) => {
  const [open, setOpen] = useState(false);

  const initialOptions = options.filter((o) =>
    INITIAL_EXPENSE_TYPES.includes(o.value)
  );
  const ongoingOptions = options.filter(
    (o) => !INITIAL_EXPENSE_TYPES.includes(o.value)
  );

  const selectedLabel =
    options.find((o) => o.value === value)?.label || "";
  const selectedColor = value && getColor ? getColor(value) : null;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="h-auto min-h-9 w-full justify-between border-0 shadow-none focus:ring-0 bg-transparent text-left whitespace-normal px-2 overflow-hidden"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              {selectedColor && selectedLabel ? (
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium break-words whitespace-normal" style={{ backgroundColor: selectedColor.bg, color: selectedColor.text }}>{selectedLabel}</span>
              ) : (
                selectedLabel || <span className="text-muted-foreground">Select type…</span>
              )}
            </span>
            <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[240px] p-0 bg-popover"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search types…" />
            <CommandList>
              <CommandEmpty>No type found.</CommandEmpty>
              {initialOptions.length > 0 && (
                <CommandGroup heading="Initial Expenses">
                  {initialOptions.map((option) => {
                    const color = getColor ? getColor(option.value) : null;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={(currentValue) => {
                          onChange(currentValue);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-1 h-4 w-4 shrink-0",
                            value === option.value
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        <span className="rounded px-1.5 py-0.5 text-xs font-medium ml-1" style={color ? { backgroundColor: color.bg, color: color.text } : undefined}>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {ongoingOptions.length > 0 && (
                <CommandGroup heading="Ongoing Expenses">
                  {ongoingOptions.map((option) => {
                    const color = getColor ? getColor(option.value) : null;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={(currentValue) => {
                          onChange(currentValue);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-1 h-4 w-4 shrink-0",
                            value === option.value
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        <span className="rounded px-1.5 py-0.5 text-xs font-medium ml-1" style={color ? { backgroundColor: color.bg, color: color.text } : undefined}>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {onManage && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 opacity-50 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onManage();
          }}
        >
          <Settings className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};
